import { Alert, Button, Card, CardBody, CardHeader, FormFeedback, FormGroup, Input, Label, Table } from 'reactstrap';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
    CoreAzureContainerAppsModuleConfiguration,
    emptyGuid,
    hasInvalidNameCharacter,
    isGuid,
} from '../../../../../../models/CoreAzureContainerAppsModuleModels';

interface AzureContainerAppsModuleConfigurationProps {
    disabled: boolean
    configuration: CoreAzureContainerAppsModuleConfiguration
    active: boolean
    updatePart: (part: Partial<CoreAzureContainerAppsModuleConfiguration>) => void
    onValidChange: (arg: boolean) => void
}

export const validator = (configuration: CoreAzureContainerAppsModuleConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (!isGuid(configuration.subscriptionId)) {
        validation.push('subscriptionId');
    }

    if (!configuration.resourceGroupName?.trim() || hasInvalidNameCharacter(configuration.resourceGroupName)) {
        validation.push('resourceGroupName');
    }

    if (!configuration.jobName?.trim() || hasInvalidNameCharacter(configuration.jobName)) {
        validation.push('jobName');
    }

    if (!configuration.targetEnvironmentId?.trim()) {
        validation.push('targetEnvironmentId');
    }

    if (active && !isGuid(configuration.tenantId)) {
        validation.push('tenantId');
    }

    return validation;
};

export const warningMessages = (
    originalConfiguration: CoreAzureContainerAppsModuleConfiguration,
    updatedConfiguration: CoreAzureContainerAppsModuleConfiguration) => {
    const warnings: string[] = [];

    if (!originalConfiguration.sourceEnvironment) {
        return warnings;
    }

    const rebindingFields: (keyof CoreAzureContainerAppsModuleConfiguration)[] = [
        'subscriptionId',
        'resourceGroupName',
        'jobName',
        'targetEnvironmentId',
    ];

    if (rebindingFields.some(f => originalConfiguration[f] !== updatedConfiguration[f])) {
        warnings.push('Om du ändrar jobbet eller miljö-ID:t kommer modulen att vägra köra tills dess trackings har setts över');
    }

    return warnings;
};

const AzureContainerAppsModuleConfiguration = (props: AzureContainerAppsModuleConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false);
    }, [props.configuration]);

    const invalid = (key: string) => validation.includes(key);

    const injectedVariables: [string, string | undefined][] = [
        ['FLOWSYNC_CUSTOMER_ID', t('azureContainerApps.injected.resolvedAtRuntime')],
        ['FLOWSYNC_INSTANCE_URL', t('azureContainerApps.injected.resolvedAtRuntime')],
        ['FLOWSYNC_MODULE_ID', t('azureContainerApps.injected.resolvedAtRuntime')],
        ['FLOWSYNC_MODULE_INSTANCE_ID', t('azureContainerApps.injected.perRun')],
        ['FLOWSYNC_MODULE_TYPE_NAME', 'AzureContainerAppsModule'],
        ['FLOWSYNC_SOURCE_ENVIRONMENT', props.configuration.sourceEnvironment ?? t('azureContainerApps.injected.setOnFirstSave')],
        ['FLOWSYNC_DRY_RUN', t('azureContainerApps.injected.perRun')],
        ['TARGET_ENVIRONMENT_ID', props.configuration.targetEnvironmentId],
    ];

    return (
        <Card className="edit-module">
            <CardHeader>
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                <span>
                    {t('modules.AzureContainerAppsModule.title', { ns: 'common' })}
                    <a href="https://flowsync.se/help/modules/export/azure-container-apps" target="_blank"><FontAwesomeIcon className="documentation-icon" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a>
                </span>
            </CardHeader>
            {open &&
                <CardBody>
                    <Alert color="info">
                        {t('azureContainerApps.dryRunHelp')}
                    </Alert>

                    <FormGroup>
                        <Label for="acamSubscriptionId">{t('azureContainerApps.subscriptionId')}</Label>
                        <Input
                            id="acamSubscriptionId"
                            type="text"
                            disabled={props.disabled}
                            invalid={invalid('subscriptionId')}
                            value={props.configuration.subscriptionId === emptyGuid ? '' : props.configuration.subscriptionId ?? ''}
                            onChange={e => props.updatePart({ subscriptionId: e.target.value })}
                        />
                        <FormFeedback>{t('azureContainerApps.subscriptionIdInvalid')}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="acamResourceGroupName">{t('azureContainerApps.resourceGroupName')}</Label>
                        <Input
                            id="acamResourceGroupName"
                            type="text"
                            maxLength={90}
                            disabled={props.disabled}
                            invalid={invalid('resourceGroupName')}
                            value={props.configuration.resourceGroupName ?? ''}
                            onChange={e => props.updatePart({ resourceGroupName: e.target.value })}
                        />
                        <FormFeedback>{t('azureContainerApps.resourceGroupNameInvalid')}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="acamJobName">{t('azureContainerApps.jobName')}</Label>
                        <Input
                            id="acamJobName"
                            type="text"
                            maxLength={200}
                            disabled={props.disabled}
                            invalid={invalid('jobName')}
                            value={props.configuration.jobName ?? ''}
                            onChange={e => props.updatePart({ jobName: e.target.value })}
                        />
                        <FormFeedback>{t('azureContainerApps.jobNameInvalid')}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="acamTargetEnvironmentId">{t('azureContainerApps.targetEnvironmentId')}</Label>
                        <Input
                            id="acamTargetEnvironmentId"
                            type="text"
                            maxLength={64}
                            disabled={props.disabled}
                            invalid={invalid('targetEnvironmentId')}
                            value={props.configuration.targetEnvironmentId ?? ''}
                            onChange={e => props.updatePart({ targetEnvironmentId: e.target.value })}
                        />
                        <FormFeedback>{t('azureContainerApps.targetEnvironmentIdInvalid')}</FormFeedback>
                        <small className="form-text text-muted">{t('azureContainerApps.targetEnvironmentIdHelp')}</small>
                    </FormGroup>

                    {invalid('tenantId') &&
                        <Alert color="warning">
                            {t('azureContainerApps.tenantMissing')}
                        </Alert>
                    }

                    <FormGroup>
                        <Label>{t('azureContainerApps.sourceEnvironment')}</Label>
                        <Input
                            type="text"
                            readOnly
                            disabled
                            value={props.configuration.sourceEnvironment ?? ''}
                        />
                        <small className="form-text text-muted">{t('azureContainerApps.sourceEnvironmentHelp')}</small>
                    </FormGroup>
                </CardBody>
            }
        </Card>
    );
};

export default AzureContainerAppsModuleConfiguration;
